import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { ScenarioInput } from 'src/graphql/_generated';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { formatCurrency } from 'src/utils/misc';
import { InfoCircleSvg, ArrowLeftSvg } from 'src/assets/dynamicSvg';
import { Tooltip } from 'src/common/Tooltip';
import MaterialInput from 'src/components/form/MaterialInput';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { SourcesSelect, EventsSelect, ConsequencesSelect } from 'src/components/select';
import { RiskSources, RiskEvents, RiskConsequences } from 'src/api/types';
import { useRiskIntel } from '../useRiskIntel';
import { useVariablesPopover } from '../useVariablesPopover';
import { useLoadingAction } from '../useLoadingAction';
import { annualChancePercent, timesYearsToAnnualized } from '../util/frequency';
import { CharCounter } from '../comps/CharCounter';
import { ReviewSection } from '../comps/ReviewSection';
import { IntelFormulaView } from '../comps/IntelFormulaView';
import { PrimaryButton, SecondaryButton } from '../comps/SuggestionButtons';

const MAX = 2000; // notes/narrative cap — comfortably under the BE columns (notes VARCHAR(2048), narrative longText)
const NAME_MAX = 255; // scenario `name` is a VARCHAR(255) DB column — must not exceed it

const Banner = styled(PageCard)`
  background: ${({ theme }) => `${theme.colors.aliceBlue}4d`};
  border-radius: 8px;
  padding: 24px 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.stroke};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  row-gap: 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LabelText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const surface = css`
  background: ${({ theme }) => theme.colors.cultured};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 3px;
`;

const Dashed = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px dashed ${({ theme }) => theme.colors.stroke};
  border-radius: 3px;
  padding: 8px 16px;
`;

const FormulaField = styled.div`
  ${surface};
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 16px;
`;

const StepperRow = styled.div`
  display: flex;
`;

const Half = styled.div<{ $right?: boolean }>`
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 48px;
  padding: 6px 16px;
  background: ${({ theme }) => theme.colors.cultured};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  ${({ $right }) => ($right ? 'border-left: none; border-radius: 0 3px 3px 0;' : 'border-radius: 3px 0 0 3px;')}
`;

const StepBtns = styled.div`
  display: flex;
  gap: 2px;
  margin-left: auto;
`;

const StepBtn = styled.button<{ $right?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: ${({ theme }) => theme.colors.white};
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  border-radius: ${({ $right }) => ($right ? '0 3px 3px 0' : '3px 0 0 3px')};
  transition:
    background 0.12s ease,
    color 0.12s ease,
    box-shadow 0.12s ease;

  &:hover {
    /* aliceBlue, not the track's own cultured grey (which dissolved the button) — flat, app-standard. */
    background: ${({ theme }) => theme.colors.aliceBlue};
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.gp1};
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 32px;

  & > * {
    flex: 1 1 0;
  }
`;

// TODO: replace placeholder `dataHtml` with each field's real help copy.
const Marker: React.FC<{ id: string }> = ({ id }) => (
  <Tooltip dataId={`ai-review-tt-${id}`} dataHtml={`TODO: ${id}-tooltip`} place="top" inline>
    <InfoCircleSvg size={14} color="#6A7590" style={{ display: 'block', flexShrink: 0 }} />
  </Tooltip>
);

const Stepper: React.FC<{ value: number; unit: string; right?: boolean; onChange: (v: number) => void }> = ({
  value,
  unit,
  right,
  onChange,
}) => (
  <Half $right={right}>
    <Poppins px={16} weight={500} color="prussianBlue">
      {value}
    </Poppins>
    <Poppins px={16} color="prussianBlue">
      {unit}
    </Poppins>
    <StepBtns>
      <StepBtn type="button" aria-label={`Decrease ${unit}`} onClick={() => onChange(Math.max(1, value - 1))}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </StepBtn>
      <StepBtn $right type="button" aria-label={`Increase ${unit}`} onClick={() => onChange(value + 1)}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </StepBtn>
    </StepBtns>
  </Half>
);

interface ReviewStepProps {
  onCreateScenario: (data: Partial<ScenarioInput>) => Promise<boolean>;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ onCreateScenario }) => {
  const { session, pending, service, backToIntel, complete } = useRiskIntel();
  const { openVariables, popoverNode } = useVariablesPopover();

  const base = useMemo(() => (session ? service.toScenarioInput(session) : {}), [service, session]);
  const cycle =
    session?.cycles.find((c) => c.id === session.selectedCycleId) ?? session?.cycles[(session?.cycles.length ?? 1) - 1];
  const risk = cycle?.risk;
  const variables = session?.variables ?? [];
  const selected = (session?.impacts ?? []).filter((i) => i.selected && i.formula);

  const [name, setName] = useState(risk?.name ?? '');
  const [narrative, setNarrative] = useState(risk?.narrative ?? '');
  const [times, setTimes] = useState(base.frequencyTimes ?? 1);
  const [years, setYears] = useState(base.frequencyYears ?? 1);
  const [freqNote, setFreqNote] = useState(base.frequencyNote ?? '');
  const [consNote, setConsNote] = useState(base.consequenceNote ?? '');
  const { loadingKey, run } = useLoadingAction();

  if (!risk) return null;

  const chancePct = annualChancePercent(timesYearsToAnnualized(times, years));
  // A null/NaN computed value means a variable is unpopulated; summing as £0 understates the total, so
  // show "—" until every selected impact resolves to a finite figure.
  const lowerComplete = selected.length > 0 && selected.every((i) => Number.isFinite(i.formula?.computedLower));
  const upperComplete = selected.length > 0 && selected.every((i) => Number.isFinite(i.formula?.computedUpper));
  const lowerResult = selected.reduce((sum, i) => sum + (i.formula?.computedLower ?? 0), 0);
  const upperResult = selected.reduce((sum, i) => sum + (i.formula?.computedUpper ?? 0), 0);

  // complete() clears the resume pointer so reopening starts fresh, not back in this review.
  const onConfirm = () =>
    run('confirm', async () => {
      const ok = await onCreateScenario({
        ...base,
        name,
        riskNarrative: narrative,
        frequencyTimes: times,
        frequencyYears: years,
        frequencyNote: freqNote,
        consequenceNote: consNote,
      });
      if (ok) complete();
    });

  const busy = pending || loadingKey != null;

  return (
    <div>
      <Banner>
        <InfoCircleSvg size={24} gradient style={{ flexShrink: 0, marginTop: 2 }} />
        <BannerText>
          <Poppins px={16} weight={500} color="prussianBlue">
            Here is a preview of your scenario for your review
          </Poppins>
          <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
            Take a final look and make edits you need. Once it looks right, click confirm to add this risk to your
            assessment.
          </Poppins>
        </BannerText>
      </Banner>

      <Spacer $px={32} />

      <Stack>
        <ReviewSection label="Risk Details">
          <Grid>
            <Field>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Title
                </Poppins>
              </LabelText>
              <AutoGrowingInput
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Add a scenario"
                css="width: 100%;"
                inputProps={{ maxLength: NAME_MAX }}
                data-cy="ai-review-title"
              />
            </Field>
            <Field>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Source
                </Poppins>
                <Marker id="source" />
              </LabelText>
              <SourcesSelect value={(base.source ?? null) as RiskSources | null} isDisabled />
            </Field>
            <Field>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Event
                </Poppins>
                <Marker id="event" />
              </LabelText>
              <EventsSelect value={(base.event ?? null) as RiskEvents | null} isDisabled />
            </Field>
            <Field>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Consequence
                </Poppins>
                <Marker id="consequence" />
              </LabelText>
              <ConsequencesSelect value={(base.consequence ?? null) as RiskConsequences | null} isDisabled />
            </Field>
          </Grid>
        </ReviewSection>

        <Divider />

        <ReviewSection label="Risk Narrative">
          <Field>
            <LabelRow>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Narrative
                </Poppins>
                <Marker id="narrative" />
              </LabelText>
              <CharCounter value={narrative} max={MAX} />
            </LabelRow>
            <MaterialInput
              name="riskNarrative"
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              placeholder="Enter your text"
              css="width: 100%;"
              multiline
              minRows={3}
              maxRows={10}
              inputProps={{ maxLength: MAX }}
              data-cy="ai-review-narrative"
            />
          </Field>
        </ReviewSection>

        <Divider />

        <ReviewSection label="Frequency Quantification">
          <Field>
            <LabelText>
              <Poppins px={16} weight={500} color="prussianBlue">
                Frequency
              </Poppins>
              <Marker id="frequency" />
            </LabelText>
            <StepperRow>
              <Stepper value={times} unit="times" onChange={setTimes} />
              <Stepper value={years} unit="years" right onChange={setYears} />
            </StepperRow>
          </Field>
          <Spacer $px={24} />
          <Dashed css="width: 100%;">
            <Poppins px={16} weight={500} color="cflowerBlue">
              The expected chance to happen in the next year: {chancePct}%
            </Poppins>
          </Dashed>
          <Spacer $px={24} />
          <Field>
            <LabelRow>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Frequency Note
                </Poppins>
                <Marker id="frequency-note" />
              </LabelText>
              <CharCounter value={freqNote} max={MAX} />
            </LabelRow>
            <MaterialInput
              name="frequencyNote"
              value={freqNote}
              onChange={(e) => setFreqNote(e.target.value)}
              placeholder="Enter your text"
              css="width: 100%;"
              multiline
              minRows={3}
              maxRows={10}
              inputProps={{ maxLength: MAX }}
            />
          </Field>
        </ReviewSection>

        <Divider />

        <ReviewSection label="Impact Quantification">
          <Field>
            <LabelText>
              <Poppins px={16} weight={500} color="prussianBlue">
                Consequence Lower
              </Poppins>
              <Marker id="consequence-lower" />
            </LabelText>
            <FormulaField>
              <Poppins px={16} color="prussianBlue">
                £
              </Poppins>
              {base.lowerFormula ? (
                <IntelFormulaView
                  raw={base.lowerFormula}
                  variables={variables}
                  onVariableClick={(id, el) => openVariables(el, id)}
                />
              ) : null}
            </FormulaField>
            <Spacer $px={8} />
            <Dashed>
              <InfoCircleSvg size={14} color="#6A7590" />
              <Poppins px={16} weight={500} color="prussianBlue">
                Result: {lowerComplete ? formatCurrency(lowerResult) : '—'}
              </Poppins>
            </Dashed>
          </Field>
          <Spacer $px={24} />
          <Field>
            <LabelText>
              <Poppins px={16} weight={500} color="prussianBlue">
                Consequence Upper
              </Poppins>
              <Marker id="consequence-upper" />
            </LabelText>
            <FormulaField>
              <Poppins px={16} color="prussianBlue">
                £
              </Poppins>
              {base.upperFormula ? (
                <IntelFormulaView
                  raw={base.upperFormula}
                  variables={variables}
                  onVariableClick={(id, el) => openVariables(el, id)}
                />
              ) : null}
            </FormulaField>
            <Spacer $px={8} />
            <Dashed>
              <InfoCircleSvg size={14} color="#6A7590" />
              <Poppins px={16} weight={500} color="prussianBlue">
                Result: {upperComplete ? formatCurrency(upperResult) : '—'}
              </Poppins>
            </Dashed>
          </Field>
          <Spacer $px={24} />
          <Field>
            <LabelRow>
              <LabelText>
                <Poppins px={16} weight={500} color="prussianBlue">
                  Consequence Note
                </Poppins>
                <Marker id="consequence-note" />
              </LabelText>
              <CharCounter value={consNote} max={MAX} />
            </LabelRow>
            <MaterialInput
              name="consequenceNote"
              value={consNote}
              onChange={(e) => setConsNote(e.target.value)}
              placeholder="Enter your text"
              css="width: 100%;"
              multiline
              minRows={3}
              maxRows={10}
              inputProps={{ maxLength: MAX }}
            />
          </Field>
        </ReviewSection>
      </Stack>

      <Spacer $px={40} />

      <Footer>
        <PrimaryButton
          label="Confirm & Proceed"
          loading={loadingKey === 'confirm'}
          disabled={busy}
          onClick={onConfirm}
        />
        <SecondaryButton
          label="BACK TO BUILDER"
          icon={<ArrowLeftSvg size={22} />}
          loading={loadingKey === 'back'}
          disabled={busy}
          onClick={() => run('back', () => backToIntel())}
        />
      </Footer>
      {popoverNode}
    </div>
  );
};
